'use strict'

import { getMessage } from '../../../../common/utils/MessageUtils'
import { SINGLE_FILE_TYPE_TARGET_GDK_CODE_SET } from './constants'
import { getItemMappingFileType } from './helpers'

// mapping_info を再帰探索し、col('fileType', ...) AST の第一引数を集める。
// 条件式・四則演算の AST は JSON 文字列で保存されているため、文字列も JSON.parse して再帰する。
function _walkForFileTypes(node, acc) {
  if (node == null) return

  if (typeof node === 'string') {
    if (node === '') return
    try {
      _walkForFileTypes(JSON.parse(node), acc)
    } catch {
      // AST ではない文字列はスキップ
    }
    return
  }

  if (Array.isArray(node)) {
    node.forEach((child) => _walkForFileTypes(child, acc))
    return
  }

  if (typeof node !== 'object') return

  if (node.type === 'CallExpression' && node.callee?.name === 'col' && node.arguments?.[0]?.type === 'Literal') {
    const value = String(node.arguments[0].value ?? '').trim()
    if (value !== '') acc.add(value)
    return
  }

  for (const value of Object.values(node)) {
    _walkForFileTypes(value, acc)
  }
}

/**
 * @param {{
 *   mappingData?: Record<string, { items?: unknown[] }>
 *   genericColumns?: { genericTaskCode?: string; chrofyColumnCode?: string; fieldKey?: string }[]
 *   dataKindCode?: string
 *   dataKindName?: string
 * }} payload
 * @returns {string | null} 対象 GDK 分類で複数ファイル種別を検出した場合のエラーメッセージ、それ以外は null
 */
export function validateSingleFileType(payload) {
  const dataKindCode = String(payload?.dataKindCode ?? '').trim()
  if (!dataKindCode || !SINGLE_FILE_TYPE_TARGET_GDK_CODE_SET.has(dataKindCode)) return null

  const mappingData = payload?.mappingData ?? {}
  const genericColumns = payload?.genericColumns ?? []
  const fileTypes = new Set()

  for (const column of genericColumns) {
    if (!column || String(column.genericTaskCode ?? '') !== dataKindCode) continue
    const fieldKey = column.fieldKey ?? `${column.genericTaskCode}_${column.chrofyColumnCode}`
    const items = mappingData[fieldKey]?.items
    if (!Array.isArray(items)) continue
    for (const item of items) {
      if (!item || typeof item !== 'object') continue
      const directType = getItemMappingFileType(item)
      if (directType) fileTypes.add(String(directType).trim())
      _walkForFileTypes(item.mapping_info, fileTypes)
    }
  }

  if (fileTypes.size <= 1) return null
  const dataKindName = String(payload?.dataKindName ?? '').trim()
  return getMessage('E011022', dataKindName || dataKindCode)
}

/** 条件マッピングの日付入力プレースホルダー（CHROFY 標準形式） */
export const CONDITION_MAPPING_DATE_PLACEHOLDER = 'YYYY-MM-DD'

/** 条件マッピングの日付形式エラーメッセージ */
export const CONDITION_MAPPING_DATE_FORMAT_ERROR = `${CONDITION_MAPPING_DATE_PLACEHOLDER}の形式を入力してください。`

const ISO_DATE_PATTERN = /^(\d{4})-(\d{1,2})-(\d{1,2})$/
const SLASH_DATE_PATTERN = /^(\d{4})\/(\d{1,2})\/(\d{1,2})$/

/**
 * 条件マッピングの日付入力を検証する（`YYYY-MM-DD` / `YYYY/MM/DD` を許容）
 *
 * 登録時は両形式を許容するが正規化はしない。CHROFY 標準形式でない値は 入力された形式のまま保存し、データ検証（プレビュー）側でエラーを検出させる。
 *
 * @param {unknown} raw
 * @returns {boolean} 実在する日付なら true
 */
export function isValidConditionMappingDateValue(raw) {
  const trimmed = String(raw ?? '').trim()
  const matched = ISO_DATE_PATTERN.exec(trimmed) ?? SLASH_DATE_PATTERN.exec(trimmed)
  if (!matched) return false

  const [, year, month, day] = matched
  const y = Number(year)
  const m = Number(month)
  const d = Number(day)
  const date = new Date(y, m - 1, d)
  return date.getFullYear() === y && date.getMonth() === m - 1 && date.getDate() === d
}
